import "./MyCampus.scss";
import CampusBox from "../../content/Campus/CampusBox/CampusBox";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import eventsService from "../../../Axios/services/events.service";
import { ISeminar } from "../../../models/seminar";
import { ISession, ITeamMember } from "../../../models/session";
import { setCampus } from "../../../store/actions/seminarActions";
import { useDispatch } from "react-redux";
import CampusSkeleton from "./components/CampusSkeleton";

interface SortedByTime {
  current: any[];
  future: any[];
  past: any[];
}

const MyCampus = () => {
  const dispatch = useDispatch();
  const { me } = useSelector((state: any) => state.auth);
  const { campus } = useSelector((state: any) => state.seminar);
  const [itemList, setItemList] = useState<any[] | undefined>(undefined);
  const [sorted, setSorted] = useState<SortedByTime>({ current: [], future: [], past: [] });

  const [t] = useTranslation("common");

  useEffect(() => {
    if (me) {
      fetchCourses();
    }
  }, [me, campus]);

  const fetchCourses = async () => {
    const campusData = campus || (await eventsService.getCourses(me._id));    
    dispatch(setCampus(campusData));

    try {
      if (campusData) {
        setItemList(campusData);
        setSorted(getSortedItems(campusData));
      } else {
        setItemList([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getSortedItems = (items: any[]): SortedByTime => {
    const current: any[] = [];
    const future: any[] = [];
    const past: any[] = [];

    items.forEach((i) => {
      const today = new Date(moment(new Date()).format("MM/DD/YYYY"));
      const startDate = new Date(moment(i.dtStartDate).format("MM/DD/YYYY"));
      const endDate = new Date(moment(i.dtEndDate).format("MM/DD/YYYY"));
      if (today < startDate && i.eventTypeId !== "Course") {
        future.push(i);
      } else if (today > endDate && i.eventTypeId !== "Course") {
        past.push(i);
      } else if (i.eventTypeId !== "Course") {
        current.push(i);
      }
    });

    return {
      current,
      future,
      past,
    };
  };

  const renderCourses = () => {
    const sortByEndDateDesc = (a: { dtEndDate: string }, b: { dtEndDate: string }) => {
      const dateA = new Date(a.dtEndDate).getTime();
      const dateB = new Date(b.dtEndDate).getTime();

      return dateB - dateA;
    };

    // Sorting the past courses by end date
    const sortedPastEnglish = sorted.past.filter((c) => c.language === "English").sort(sortByEndDateDesc);

    const sortedPastHebrew = sorted.past.filter((c) => c.language === "Hebrew").sort(sortByEndDateDesc);

    const sortedPastArabic = sorted.past.filter((c) => c.language === "Arabic").sort(sortByEndDateDesc);

    return Array.isArray(itemList) && itemList.length > 0 ? (
      <>
        {sorted.current.length > 0 && (
          <>
            <div className="mx-md-0 my-4 px-md-0 px-3">
              <div className="title">{t("My Learnings")}</div>
            </div>
            <div className="d-flex justify-content-start flex-wrap px-md-0 px-3">
              {sorted.current.map((c: any, index: number) => (
                <div key={c._id || `current-${index}`} className="px-2 py-2 flex-grow-1 flex-md-grow-0">
                  <CampusBox item={c} />
                </div>
              ))}
            </div>
          </>
        )}
        {sorted.future.length > 0 && (
          <>
            <div className="mx-md-0 my-4 px-md-0 px-3">
              <div className="title future-learnings-title">{t("Future Learnings")}</div>
            </div>
            <div className="d-flex justify-content-start flex-wrap px-md-0 px-3">
              {sorted.future.map((c: any, index: number) => (
                <div key={c.id || `future-${index}`} className="px-2 py-2">
                  <CampusBox item={c} />
                </div>
              ))}
            </div>
          </>
        )}
        {sorted.past.length > 0 && (
          <>
            {sortedPastEnglish.length > 0 && (
              <div className="mx-md-0 my-2 px-md-0 px-3">
                <div className="title">{t("Past Seminars")}</div>
              </div>
            )}
            <div className="d-flex justify-content-start flex-wrap px-md-0 px-3">
              {sortedPastEnglish.map((c: any, index: number) => (
                <div key={c._id || `past-${index}`} className="px-2 py-2 animate__animated animate__pulse">
                  <CampusBox item={c} type={"past"} />
                </div>
              ))}
            </div>
          </>
        )}

        {sorted.past.length > 0 && (
          <>
            {sortedPastHebrew.length > 0 && (
              <div className="mx-md-0 my-2 px-md-0 px-3">
                <div className="title pastRTL">{t("סמינרים - עברית")}</div>
              </div>
            )}
            <div className="d-flex justify-content-start flex-wrap flex-column px-md-0 px-3">
              {sortedPastHebrew.map((c: any) => (
                <div key={c._id} className="px-2 py-2 animate__animated animate__pulse">
                  <CampusBox item={c} type="past" isPastHebrew />
                </div>
              ))}
            </div>
          </>
        )}

        {sorted.past.length > 0 && (
          <>
            {sortedPastArabic.length > 0 && (
              <div className="mx-md-0 my-2 px-md-0 px-3">
                <div className="title pastRTL">{t("ندوات - عربي")}</div>
              </div>
            )}
            <div className="d-flex justify-content-start flex-wrap px-md-0 px-3">
              {sortedPastArabic.map((c: any) => (
                <div key={c._id} className="px-2 py-2 animate__animated animate__pulse">
                  <CampusBox item={c} type={"past"} />
                </div>
              ))}
            </div>
          </>
        )}
      </>
    ) : (
      <div className="mx-md-0 mb-y px-md-0 px-3">You don't have any courses yet.</div>
    );
  };

  return (
    <div className="my-campus container-fluid px-md-3 px-0">
      {itemList ? renderCourses() : <CampusSkeleton />}
    </div>
  )
};

export default MyCampus;
